import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts, fetchCategories } from '../../store/slices/productSlice';
import Spinner from '../../components/common/Spinner';
import imgUrl from '../../utils/imgUrl';
import useIsMobile from '../../hooks/useIsMobile';

export default function Products() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { products, categories, loading, totalPages } = useSelector((s) => s.product);
  const isMobile = useIsMobile();
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    min_price: '', max_price: '', search: '', sort: 'newest', page: 1,
  });

  useEffect(() => { dispatch(fetchCategories()); }, [dispatch]);
  useEffect(() => { dispatch(fetchProducts({ ...filters, limit: 12 })); }, [dispatch, filters]);

  const update = (key, val) => setFilters((f) => ({ ...f, [key]: val, page: 1 }));

  const FilterPanel = (
    <aside style={{ ...S.sidebar, width: isMobile ? '100%' : '220px' }}>
      <p style={S.sideTitle}>FILTER</p>

      <div style={S.filterGroup}>
        <p style={S.filterLabel}>Search</p>
        <input style={S.input} placeholder="Search products..." value={filters.search}
          onChange={(e) => update('search', e.target.value)} />
      </div>

      <div style={S.filterGroup}>
        <p style={S.filterLabel}>Category</p>
        <select style={S.input} value={filters.category} onChange={(e) => update('category', e.target.value)}>
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div style={S.filterGroup}>
        <p style={S.filterLabel}>Price Range</p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input style={{ ...S.input, marginBottom: 0 }} type="number" placeholder="Min ₹" value={filters.min_price}
            onChange={(e) => update('min_price', e.target.value)} />
          <input style={{ ...S.input, marginBottom: 0 }} type="number" placeholder="Max ₹" value={filters.max_price}
            onChange={(e) => update('max_price', e.target.value)} />
        </div>
      </div>

      <div style={S.filterGroup}>
        <p style={S.filterLabel}>Sort By</p>
        <select style={S.input} value={filters.sort} onChange={(e) => update('sort', e.target.value)}>
          <option value="newest">Newest First</option>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>

      <button style={S.clearBtn} onClick={() => { setFilters({ category: '', min_price: '', max_price: '', search: '', sort: 'newest', page: 1 }); setShowFilters(false); }}>
        Clear Filters
      </button>
    </aside>
  );

  return (
    <div className="page-top">
      {/* Page Header */}
      <div style={{ ...S.pageHeader, padding: isMobile ? '32px 18px' : '48px 56px' }}>
        <p className="sec-label">Browse</p>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px,5vw,56px)', letterSpacing: '2px', color: '#fff', lineHeight: 1 }}>
          ALL <span style={{ color: 'var(--gold)' }}>PRODUCTS</span>
        </h1>
      </div>

      {/* Mobile filter toggle */}
      {isMobile && (
        <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button style={{ ...S.clearBtn, width: 'auto', padding: '8px 18px' }} onClick={() => setShowFilters((s) => !s)}>
            {showFilters ? 'Hide Filters ▲' : 'Show Filters ▼'}
          </button>
          <select style={{ ...S.input, flex: 1 }} value={filters.sort} onChange={(e) => update('sort', e.target.value)}>
            <option value="newest">Newest First</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="name">Name A–Z</option>
          </select>
        </div>
      )}

      <div style={{ ...S.wrap, flexDirection: isMobile ? 'column' : 'row', padding: isMobile ? '20px 18px' : '40px 56px' }}>
        {/* Sidebar Filters */}
        {(!isMobile || showFilters) && FilterPanel}

        {/* Product Grid */}
        <div style={{ flex: 1 }}>
          {loading ? <Spinner /> : (
            <>
              <div style={{ ...S.grid, gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: isMobile ? '14px' : '24px' }}>
                {products.length === 0 && (
                  <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px', color: '#888' }}>
                    <p style={{ fontSize: '40px', marginBottom: '12px' }}>🔍</p>
                    <p style={{ fontFamily: 'var(--font-display)', fontSize: '24px', letterSpacing: '2px' }}>No products found</p>
                  </div>
                )}
                {products.map((p) => (
                  <Link key={p.id} to={`/products/${p.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="product-card">
                      <div className="product-img">
                        {p.compare_price && <span className="p-tag p-tag-red">SALE</span>}
                        {p.stock === 0 && <span style={{ ...S.outTag }}>OUT OF STOCK</span>}
                        <div className="product-img-inner">
                          {p.image
                            ? <img src={imgUrl(p.image)} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <span style={{ fontSize: '64px' }}>👕</span>}
                        </div>
                      </div>
                      <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--red)', marginBottom: '3px' }}>{p.category_name}</p>
                      <p className="p-name">{p.name}</p>
                      <p className="p-price">
                        ₹{p.price}
                        {p.compare_price && <span className="p-price-orig">₹{p.compare_price}</span>}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={S.pagination}>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button key={i} style={{ ...S.pageBtn, background: filters.page === i + 1 ? 'var(--black)' : '#fff', color: filters.page === i + 1 ? '#fff' : 'var(--black)' }}
                      onClick={() => setFilters((f) => ({ ...f, page: i + 1 }))}>
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const S = {
  pageHeader: { background: 'var(--black)', padding: '48px 56px', borderBottom: '3px solid var(--gold)' },
  wrap:       { display: 'flex', gap: '32px', maxWidth: '1400px', margin: '0 auto' },
  sidebar:    { flexShrink: 0 },
  sideTitle:  { fontFamily: 'var(--font-display)', fontSize: '16px', letterSpacing: '3px', marginBottom: '24px', borderBottom: '2px solid var(--red)', paddingBottom: '10px' },
  filterGroup:{ marginBottom: '20px' },
  filterLabel:{ fontSize: '10px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#888', marginBottom: '8px' },
  input:      { width: '100%', padding: '9px 12px', border: '1.5px solid var(--border)', background: '#fff', fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none', marginBottom: '0', boxSizing: 'border-box' },
  clearBtn:   { width: '100%', padding: '9px', background: 'none', border: '1.5px solid var(--border)', fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  grid:       { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '24px' },
  outTag:     { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,.6)', color: '#fff', textAlign: 'center', padding: '6px', fontSize: '9px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' },
  pagination: { display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '40px' },
  pageBtn:    { width: '36px', height: '36px', border: '1.5px solid var(--border)', fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all .2s' },
};
